import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { User, LogOut, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import { MobileSheet, MobileSheetBody, MobileSheetClose, MobileSheetContent, MobileSheetTitle, MobileSheetTrigger } from "./ui/mobile-sheet";
import useIsMobile from "../hooks/useIsMobile";

export default function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const nameParts = (user?.name || "").trim().split(/\s+/).filter(Boolean);
  // First + last name only: a full "Prénom Deuxième Troisième NOM" is too
  // long for the header.
  const shortName =
    nameParts.length > 1 ? `${nameParts[0]} ${nameParts[nameParts.length - 1]}` : nameParts[0] || user?.email || "";
  const initials =
    nameParts.length > 1
      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase()
      : (nameParts[0] || user?.email || "?").slice(0, 2).toUpperCase();
  const roleLabel = user?.role_label || user?.role;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const trigger = (
    <button type="button" data-testid="btn-user-menu" aria-label="Menu du profil"
      className="flex items-center gap-2 pl-1 pr-2 h-10 rounded-lg hover:bg-slate-100 transition">
      <div className="w-8 h-8 rounded-full bg-jamora text-white flex items-center justify-center text-xs font-semibold overflow-hidden shrink-0">
        {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" /> : initials}
      </div>
      <div className="hidden md:block text-left leading-tight">
        <div className="text-sm font-medium text-slate-900 truncate max-w-[140px]">{shortName}</div>
        <div className="text-2xs text-slate-500 truncate max-w-[140px]">{roleLabel}</div>
      </div>
      <ChevronDown size={16} className="hidden md:block text-slate-400" />
    </button>
  );

  if (isMobile) return (
    <MobileSheet>
      <MobileSheetTrigger asChild>{trigger}</MobileSheetTrigger>
      <MobileSheetContent closeOnHandle aria-describedby={undefined}>
        <MobileSheetTitle className="sr-only">Menu du profil</MobileSheetTitle>
        <MobileSheetBody>
          <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-full bg-jamora text-white flex items-center justify-center text-sm font-semibold overflow-hidden shrink-0">
              {user?.avatar_url ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" /> : initials}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{shortName}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              <Badge variant="secondary" className="mt-1 text-2xs font-medium">{roleLabel}</Badge>
            </div>
          </div>
          <div className="pt-2 space-y-1">
            <MobileSheetClose asChild>
              <button type="button" data-testid="menu-my-profile" onClick={() => navigate("/profile")}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-slate-700 hover:bg-slate-100">
                <User size={18} className="text-slate-500" /> Mon profil
              </button>
            </MobileSheetClose>
            <MobileSheetClose asChild>
              <button type="button" data-testid="btn-logout" onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left text-sm text-red-600 hover:bg-red-50">
                <LogOut size={18} /> Se déconnecter
              </button>
            </MobileSheetClose>
          </div>
        </MobileSheetBody>
      </MobileSheetContent>
    </MobileSheet>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3 py-1">
            <div className="w-9 h-9 rounded-full bg-jamora text-white flex items-center justify-center text-sm font-semibold overflow-hidden shrink-0">
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                initials
              )}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{shortName}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              <Badge variant="secondary" className="mt-1 text-2xs font-medium">
                {roleLabel}
              </Badge>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem data-testid="menu-my-profile" onClick={() => navigate("/profile")}>
          <User size={16} className="text-slate-400" /> Mon profil
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          data-testid="btn-logout"
          onClick={handleLogout}
          className="text-red-600 focus:text-red-600 focus:bg-red-50"
        >
          <LogOut size={16} /> Se déconnecter
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
